import React, { useState } from "react";

export default function RaspberryPiSetup() {
  const [emailButtonText, setEmailButtonText] = useState("📧 Contact for Help");

  const handleEmailClick = (e) => {
    e.preventDefault();
    const email = "sebastian-wagner-96@gmx.de";
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setEmailButtonText("📧 Email Copied!");
        setTimeout(() => {
          setEmailButtonText("📧 Contact for Help");
        }, 2000);
        window.location.href = `mailto:${email}?subject=Auto Shiny Counter - Raspberry Pi Setup Help`;
      })
      .catch(() => {
        console.log("Could not copy email to clipboard");
        window.location.href = `mailto:${email}?subject=Auto Shiny Counter - Raspberry Pi Setup Help`;
      });
  };

  return (
    <div className="app-container">
      <div className="background-pattern"></div>

      <div className="main-content">
        <div className="legal-header">
          <img
            src="/logo.png"
            alt="Auto Shiny Counter Logo"
            className="app-logo"
          />
          <h1 className="title">Auto Shiny Counter x Raspberry Pi Setup</h1>
          <p className="legal-date">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>What You Can Do With This Setup</h2>
            <p>
              Once everything is connected, you can build custom button
              sequences, map combos to single taps, and trigger the shiny
              counter automatically whenever you press your hunt button. That
              way you automate repetitive inputs, keep the counter perfectly in
              sync with each encounter, and make long Switch hunts a lot easier
              to manage.
            </p>
          </section>

          <section className="legal-section">
            <h2>What You Need</h2>
            <ul>
              <li>Raspberry Pi + SD Card (must support WiFi and Bluetooth)</li>
              <li>HDMI cable (to connect to a monitor/TV)</li>
              <li>
                Mouse and keyboard (with adapters if needed, depending on your
                Raspberry Pi model)
              </li>
              <li>Computer with SD card reader</li>
              <li>Raspberry Pi Imager (download from raspberrypi.com)</li>
              <li>Internet connection</li>
              <li>Nintendo Switch</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Step 1: Install 64-bit Desktop OS on Raspberry Pi</h2>
            <ol>
              <li>
                Download and install{" "}
                <a
                  href="https://www.raspberrypi.com/software/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Raspberry Pi Imager
                </a>{" "}
                on your computer
              </li>
              <li>Insert the SD card into your computer</li>
              <li>Open Raspberry Pi Imager</li>
              <li>Choose your Raspberry Pi model</li>
              <li>
                Click "Choose OS" and select "Raspberry Pi OS (64-bit)" with
                Desktop
              </li>
              <li>Click "Choose Storage" and select your SD card</li>
              <li>
                Click the gear icon (⚙️) to open the "General" settings and
                configure:
                <ul>
                  <li>Username and password</li>
                  <li>WiFi network name (SSID) and password</li>
                  <li>WiFi country</li>
                  <li>Language settings</li>
                </ul>
              </li>
              <li>Click "Write" to flash the OS to the SD card</li>
              <li>
                Once complete, insert the SD card into your Raspberry Pi and
                boot it up
              </li>
              <li>Enable Bluetooth on your Raspberry Pi</li>
            </ol>
          </section>

          <section className="legal-section">
            <h2>Step 2: Install and Start Switch Pro Controller Emulation</h2>
            <p>
              <strong>Tip:</strong> Open this guide in your browser on the
              Raspberry Pi for easy access to the commands:{" "}
              <a
                href="https://auto-shiny-counter.netlify.app/raspberry-setup"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://auto-shiny-counter.netlify.app/raspberry-setup
              </a>
            </p>
            <p>
              Open a terminal on your Raspberry Pi (the black icon in the top
              status bar).
            </p>
            <p>First, get your Raspberry Pi's IP address:</p>
            <div className="code-block">
              <code>hostname -I</code>
            </div>
            <p>Save this IP address - you'll need it in the app later.</p>
            <p>Now install the controller emulation:</p>
            <div className="code-block">
              <code>
                wget
                https://github.com/typenoob/nxbt/releases/download/Build_2023.10.29/nxbt-gnu-aarch64
              </code>
            </div>
            <div className="code-block">
              <code>chmod +x nxbt-gnu-aarch64</code>
            </div>
            <div className="code-block">
              <code>sudo mv nxbt-gnu-aarch64 /usr/local/bin/nxbt</code>
            </div>
            <p>Start the controller emulation:</p>
            <div className="code-block">
              <code>sudo nxbt webapp</code>
            </div>
            <p>
              When the Raspberry Pi finishes launching the webapp, the terminal
              should print <code>wsgi starting up on http://0.0.0.0:8000</code>.
              Seeing that message means the controller service is running as
              expected.
            </p>
          </section>

          <section className="legal-section">
            <h2>Step 3: Connect from Auto Shiny Counter App</h2>
            <p>
              <strong>Important:</strong> Make sure your Nintendo Switch,
              Raspberry Pi, and phone are all connected to the same WiFi
              network.
            </p>
            <ol>
              <li>Connect a controller to your phone via Bluetooth</li>
              <li>Open the Auto Shiny Counter app on your phone</li>
              <li>Enter the Raspberry Pi's IP address in the app</li>
              <li>
                Go to the Change Grip/Order Menu on your Nintendo Switch to put
                it in pairing mode (Controller Icon in the Home Menu → Change
                Grip/Order)
              </li>
              <li>Click "Connect" in the Auto Shiny Counter app</li>
              <li>Accept the pairing on the Raspberry Pi when prompted</li>
            </ol>
            <p>
              After the initial pairing, the controller should connect
              automatically in the future, though this doesn't always work
              reliably.
            </p>
          </section>

          <section className="legal-section">
            <h2>Troubleshooting</h2>
            <p>If you're having connection issues, try these solutions:</p>
            <ol>
              <li>
                <strong>Restart the Switch completely:</strong> Hold down the
                power button to fully restart the Switch. This sometimes fixes
                issues when nothing can connect anymore.
              </li>
              <li>
                <strong>Restart the Raspberry Pi completely:</strong> This helps
                with the same connection problems.
              </li>
              <li>
                <strong>Restart the controller emulation:</strong> Run the{" "}
                <code>sudo nxbt webapp</code> command again on the Raspberry Pi.
              </li>
            </ol>
            <p>If you're still having issues, please contact us for help:</p>
            <button onClick={handleEmailClick} className="feedback-button">
              {emailButtonText}
            </button>
          </section>
        </div>

        <div className="legal-footer">
          <a href="/" className="back-link">
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
